import React, { useEffect, useMemo, useRef } from 'react';
import { useChatContext } from '@/contexts/ChatContext';
import { Loader2, Plus, MessageSquare, Search, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ChatSidebarMobileProps {
  onChatSelect?: () => void;
}

export const ChatSidebarMobile: React.FC<ChatSidebarMobileProps> = ({ onChatSelect }) => {
  const {
    chatSessions,
    activeChatId,
    setActiveChatId,
    createNewChat,
    deleteSelectedChats,
    isSelectionMode,
    setSelectionMode,
    selectedChatIds,
    toggleChatSelection,
    selectAllChats,
    clearSelectedChats,
    searchTerm,
    setSearchTerm,
    isLoading,
  } = useChatContext();
  const { t } = useTranslation();

  const filteredChatSessions = useMemo(() => {
    return chatSessions.filter(chat =>
      chat.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [chatSessions, searchTerm]);

  const selectionCount = selectedChatIds.length;
  const hasChats = filteredChatSessions.length > 0;
  const allFilteredSelected =
    hasChats &&
    filteredChatSessions.every(chat => selectedChatIds.includes(chat.id));

  useEffect(() => {
    if (isSelectionMode && selectedChatIds.length === 0) {
      setSelectionMode(false);
    }
  }, [isSelectionMode, selectedChatIds, setSelectionMode]);

  const handleChatSelect = (chatId: string) => {
    setActiveChatId(chatId);
    onChatSelect?.();
  };

  const handleNewChat = async () => {
    await createNewChat();
    onChatSelect?.();
  };

  const longPressTimeoutRef = useRef<number | null>(null);

  const handleTouchStart = (chatId: string) => {
    if (longPressTimeoutRef.current !== null) {
      window.clearTimeout(longPressTimeoutRef.current);
    }
    longPressTimeoutRef.current = window.setTimeout(() => {
      longPressTimeoutRef.current = null;
      if (!isSelectionMode) {
        setSelectionMode(true);
      }
      toggleChatSelection(chatId);
    }, 450);
  };

  const cancelLongPress = () => {
    if (longPressTimeoutRef.current !== null) {
      window.clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
  };

  return (
    <div className="h-full w-full text-white overflow-y-auto bg-[hsl(var(--background))]">
      {/* Header */}
      <div className="px-4 py-6 flex flex-col gap-2 items-center">
        <h2 className="text-xl text-white text-center">{t('chats_title')}</h2>
        {chatSessions.length > 0 && selectionCount > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Button
              variant="link"
              className="px-0 text-xs font-medium text-neutral-300 underline underline-offset-4 hover:text-white"
              disabled={!hasChats}
              onClick={() => {
                if (!hasChats) {
                  return;
                }
                if (allFilteredSelected) {
                  clearSelectedChats();
                } else {
                  selectAllChats(filteredChatSessions.map(chat => chat.id));
                  setSelectionMode(true);
                }
              }}
            >
              {t('select_all_chats', 'Select all')}
            </Button>
            <Button
              variant="link"
              className="px-0 text-xs font-medium text-neutral-300 underline underline-offset-4 hover:text-white"
              disabled={selectionCount === 0}
              onClick={() => {
                clearSelectedChats();
              }}
            >
              {t('clear_selection', 'Clear selection')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-red-400 hover:text-red-500 hover:bg-red-500/10"
              disabled={selectionCount === 0}
              onClick={() => {
                if (selectionCount === 0) {
                  return;
                }
                const confirmed = window.confirm(
                  t('confirm_delete_selected_chats', {
                    count: selectionCount,
                    defaultValue:
                      selectionCount === 1
                        ? 'Delete the selected chat?'
                        : `Delete ${selectionCount} selected chats?`,
                  })
                );
                if (!confirmed) {
                  return;
                }
                void Promise.resolve(deleteSelectedChats()).then(() => {
                  setSelectionMode(false);
                });
              }}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">{t('delete_selected_chats', 'Delete selected')}</span>
            </Button>
          </div>
        )}
      </div>

      {/* New Chat Button */}
      <div className="px-4 py-4">
        <button
          type="button"
          onClick={handleNewChat}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-md text-sm font-medium text-highlight-foreground bg-highlight/80 hover:bg-highlight transition-colors"
        >
          <Plus className="h-4 w-4" />
          {t('new_chat')}
        </button>
      </div>

      {/* Search */}
      <div className="px-4 pb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white/60" />
          <input
            type="text"
            placeholder={t('search_chats')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-md text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-transparent"
          />
        </div>
      </div>

      {/* Chat List */}
      <div className="px-4 space-y-1 pb-20">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-white/60" />
          </div>
        ) : filteredChatSessions.length === 0 ? (
          <div className="text-center py-8 text-white/60">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">{searchTerm ? t('no_chats_found') : t('no_chats_yet')}</p>
          </div>
        ) : (
          filteredChatSessions.map((chat) => {
            const isActive = activeChatId === chat.id;
            const isSelected = selectedChatIds.includes(chat.id);

            return (
              <div
                key={chat.id}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (isSelectionMode) {
                    toggleChatSelection(chat.id);
                    return;
                  }
                  handleChatSelect(chat.id);
                }}
                onTouchStart={() => handleTouchStart(chat.id)}
                onTouchEnd={cancelLongPress}
                onTouchCancel={cancelLongPress}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (isSelectionMode) {
                      toggleChatSelection(chat.id);
                    } else {
                      handleChatSelect(chat.id);
                    }
                  }
                }}
                className={cn(
                  'w-full text-left px-3 py-3 rounded-md text-sm transition-colors flex items-center gap-3 cursor-pointer',
                  isSelectionMode
                    ? isSelected
                      ? 'bg-white/20 text-white'
                      : 'text-white/80 bg-white/5'
                    : isActive
                      ? 'bg-white/20 text-white'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                )}
              >
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={() => {
                    if (!isSelectionMode) {
                      setSelectionMode(true);
                    }
                    toggleChatSelection(chat.id);
                  }}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                  className={cn(
                    'border-neutral-600 data-[state=checked]:border-highlight data-[state=checked]:bg-highlight',
                    !isSelectionMode && !isSelected ? 'opacity-0' : ''
                  )}
                  aria-label={t('select_chat', {
                    title: chat.title,
                    defaultValue: `Select ${chat.title}`,
                  })}
                />
                <MessageSquare className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{chat.title}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
